import React, { useState } from "react";
import AdminTable from "../../Common/AdminTable";
import AdminDrawer from "../../Common/AdminDrawer";
import ConfirmDialog from "../../Common/ConfirmDialog";
import { recordAuditLog } from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { Eye, Shield, Lock, Unlock, Clock, CheckCircle2 } from "lucide-react";

const INITIAL_USERS = [
  {
    id: "usr_101",
    name: "Arjun Verma",
    email: "arjun.verma@example.com",
    phone: "+91 98765 43210",
    plan: "Premium Yearly",
    plan_status: "Active",
    access_expiry: "2025-05-15",
    is_blocked: false,
    tests_attempted: 18,
    created_at: "2023-10-12",
  },
  {
    id: "usr_102",
    name: "Sneha Reddy",
    email: "sneha.reddy@example.com",
    phone: "+91 98123 45678",
    plan: "Free",
    plan_status: "Active",
    access_expiry: "2024-12-31",
    is_blocked: false,
    tests_attempted: 4,
    created_at: "2024-01-05",
  },
  {
    id: "usr_103",
    name: "Rohit Deshmukh",
    email: "rohit.d@example.com",
    phone: "+91 97654 32109",
    plan: "Prelims Test Series",
    plan_status: "Expired",
    access_expiry: "2024-02-01",
    is_blocked: true,
    tests_attempted: 9,
    created_at: "2023-08-20",
  },
];

export default function UserManagementPage() {
  const { adminUser } = useAdminAuth();
  const [users, setUsers] = useState(INITIAL_USERS);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });

  // Drawer edit states
  const [editPlan, setEditPlan] = useState("");
  const [editExpiry, setEditExpiry] = useState("");

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  const handleOpenDrawer = (user) => {
    setSelectedUser(user);
    setEditPlan(user.plan);
    setEditExpiry(user.access_expiry);
  };

  const handleSaveAccess = () => {
    if (!selectedUser) return;
    const original = { ...selectedUser };
    const updated = {
      ...selectedUser,
      plan: editPlan,
      access_expiry: editExpiry,
      plan_status: new Date(editExpiry) > new Date() ? "Active" : "Expired",
    };

    setUsers(users.map((u) => (u.id === selectedUser.id ? updated : u)));
    setSelectedUser(updated);

    recordAuditLog({
      action: "User Plan/Access Modified",
      module: "Users",
      entityType: "User",
      entityId: selectedUser.id,
      beforeValue: original,
      afterValue: updated,
      adminUser: adminUser.email,
    });

    showToast(`Access updated for ${selectedUser.name}`);
  };

  const handleToggleBlock = (user) => {
    const nextBlocked = !user.is_blocked;
    setConfirmDialog({
      isOpen: true,
      title: nextBlocked ? "Block User Account" : "Unblock User Account",
      message: nextBlocked
        ? `Are you sure you want to block ${user.name}? They will be prevented from logging in.`
        : `Are you sure you want to unblock ${user.name}?`,
      confirmText: nextBlocked ? "Block User" : "Unblock User",
      confirmVariant: nextBlocked ? "danger" : "primary",
      onConfirm: () => {
        const original = { ...user };
        const updated = { ...user, is_blocked: nextBlocked };
        setUsers(users.map((u) => (u.id === user.id ? updated : u)));
        if (selectedUser?.id === user.id) setSelectedUser(updated);

        recordAuditLog({
          action: nextBlocked ? "User Blocked" : "User Unblocked",
          module: "Users",
          entityType: "User",
          entityId: user.id,
          beforeValue: original,
          afterValue: updated,
          adminUser: adminUser.email,
        });

        showToast(`User ${user.name} ${nextBlocked ? "blocked" : "unblocked"}`);
      },
    });
  };

  const columns = [
    {
      title: "User",
      key: "name",
      render: (val, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{val}</div>
          <div style={{ fontSize: "12px", color: "#64748b" }}>{row.email}</div>
        </div>
      ),
    },
    {
      title: "Phone",
      key: "phone",
      width: "140px",
      render: (val) => <span style={{ fontSize: "13px", color: "#475569" }}>{val}</span>,
    },
    {
      title: "Current Plan",
      key: "plan",
      render: (val) => (
        <span
          style={{
            fontSize: "12px",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: "4px",
            background: "#f0fdf4",
            color: "#16a34a",
            border: "1px solid #bbf7d0",
          }}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Access Expiry",
      key: "access_expiry",
      width: "120px",
      render: (val) => (
        <span style={{ fontSize: "12.5px", color: "#64748b" }}>{val}</span>
      ),
    },
    {
      title: "Status",
      key: "is_blocked",
      width: "110px",
      render: (val) =>
        val ? (
          <span className="status-badge inactive">Blocked</span>
        ) : (
          <span className="status-badge active">Active</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "140px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => handleOpenDrawer(row)}
            title="View Profile & Manage Access"
          >
            <Eye size={13} /> Manage
          </button>
          <button
            className={`admin-btn admin-btn-sm ${row.is_blocked ? "admin-btn-secondary" : "admin-btn-danger"}`}
            onClick={() => handleToggleBlock(row)}
            title={row.is_blocked ? "Unblock User" : "Block User"}
          >
            {row.is_blocked ? <Unlock size={13} /> : <Lock size={13} />}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      <AdminTable
        columns={columns}
        data={users}
        searchPlaceholder="Search users by name, email, phone, plan..."
        pageSize={10}
        rowKey="id"
      />

      {/* User Management & Plan Drawer */}
      <AdminDrawer
        isOpen={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        title={selectedUser ? `User: ${selectedUser.name}` : ""}
        width="560px"
        footer={
          <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "flex-end" }}>
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setSelectedUser(null)}
            >
              Close
            </button>
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleSaveAccess}
            >
              Save Access Changes
            </button>
          </div>
        }
      >
        {selectedUser && (
          <div>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid var(--admin-border)",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", marginBottom: "4px" }}>
                {selectedUser.name}
              </div>
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                {selectedUser.email} &bull; {selectedUser.phone}
              </div>
              <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "8px" }}>
                Registered on {selectedUser.created_at} &bull; Attempts: {selectedUser.tests_attempted}
              </div>
            </div>

            <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "12px" }}>
              User Plan & Entitlements Management
            </h4>

            <div className="admin-form-group">
              <label className="admin-form-label">Assign Plan</label>
              <select
                className="admin-form-select"
                value={editPlan}
                onChange={(e) => setEditPlan(e.target.value)}
              >
                <option value="Free">Free</option>
                <option value="Prelims Test Series">Prelims Test Series</option>
                <option value="Premium Monthly">Premium Monthly</option>
                <option value="Premium Yearly">Premium Yearly</option>
              </select>
              <span style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", display: "block" }}>
                Reuses the application's existing Plan & Entitlements model.
              </span>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Extend Access Expiry</label>
              <input
                type="date"
                className="admin-form-input"
                value={editExpiry}
                onChange={(e) => setEditExpiry(e.target.value)}
              />
            </div>

            <div
              style={{
                marginTop: "24px",
                padding: "14px",
                border: "1px solid var(--admin-border)",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                  Account Status: {selectedUser.is_blocked ? "Blocked" : "Active"}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>
                  {selectedUser.is_blocked
                    ? "User is currently restricted from platform access."
                    : "User has standard platform access."}
                </div>
              </div>

              <button
                className={`admin-btn admin-btn-sm ${selectedUser.is_blocked ? "admin-btn-secondary" : "admin-btn-danger"}`}
                onClick={() => handleToggleBlock(selectedUser)}
              >
                {selectedUser.is_blocked ? "Unblock Account" : "Block Account"}
              </button>
            </div>
          </div>
        )}
      </AdminDrawer>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
      />
    </div>
  );
}
